import React from 'react';
import SectionBadge from '../ui/SectionBadge';
import { motion } from 'framer-motion';
import {
  Building03Icon,
  Target01Icon,
  EyeIcon,
  Award01Icon,
  Location01Icon,
  Clock01Icon,
  ShieldEnergyIcon,
  SparklesIcon,
  UserGroupIcon,
} from 'hugeicons-react';

const values = [
  {
    icon: <ShieldEnergyIcon size={32} />,
    title: 'الجودة أولاً',
    description: 'لا نرضى إلا بالأفضل. نستخدم أحدث التقنيات والمواد العالمية لضمان نتائج استثنائية.',
  },
  {
    icon: <SparklesIcon size={32} />,
    title: 'الاحترافية',
    description: 'فريقنا مدرب على أعلى المعايير الدولية. خبرة سنوات في العناية بالسيارات الفارهة.',
  },
  {
    icon: <UserGroupIcon size={32} />,
    title: 'رضا العملاء',
    description: 'عميلنا هو شريكنا في النجاح. نسعى دائماً لتجاوز توقعاته وتقديم تجربة لا تُنسى.',
  },
  {
    icon: <Award01Icon size={32} />,
    title: 'التميز',
    description: 'نطمح لأن نكون الوجهة الأولى في طرابلس للعناية الفاخرة بالسيارات.',
  },
];

const milestones = [
  {
    year: '2025',
    title: 'بداية الرحلة',
    description: 'تأسيس مركز M Four في طرابلس بفريق محترف ورؤية واضحة.',
  },
  {
    year: '2025',
    title: 'شراكات عالمية',
    description: 'نعمل من خلال شراكات عالمية مع أكبر العلامات: STEK، Koch Chemie، و Tonyin.',
  },
  {
    year: '2025',
    title: 'نمو مستمر',
    description: 'خدمنا أكثر من 100 سيارة وبنينا قاعدة عملاء متميزة في طرابلس.',
  },
];

export default function AboutUs() {
  return (
    <section id="about-us" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.03]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionBadge text="من نحن" icon={Building03Icon} />
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 text-white">
            تعرف <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-600">علينا</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            في قلب طرابلس، نقدم تجربة عناية بالسيارات تجمع بين الشغف والاحترافية والمواد العالمية المتميزة.
          </p>
        </motion.div>

        {/* Main Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="relative p-8 rounded-3xl bg-[#111] border border-white/5 overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Building03Icon size={32} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">من نحن</h3>
                <p className="text-gray-400 leading-relaxed text-lg mb-4">
                  مركز <span className="text-primary font-bold">M Four</span> للعناية بالسيارات هو وجهة متخصصة في 
                  تقديم خدمات فاخرة للعناية بالسيارات في طرابلس، ليبيا. تأسسنا بفكرة بسيطة: 
                  <span className="text-white font-semibold"> سيارتك تستحق الأفضل</span>.
                </p>
                <p className="text-gray-400 leading-relaxed text-lg">
                  نحن فريق من المحترفين الشغوفين الذين يجمعون بين الخبرة المحلية والمعرفة الدولية 
                  لتقديم خدمات لا مثيل لها. نؤمن بأن كل سيارة لها قصة، وكل عميل يستحق تجربة استثنائية.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="relative p-8 rounded-3xl bg-gradient-to-br from-[#111] to-[#050505] border border-white/5 overflow-hidden group">
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Location01Icon size={32} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">موقعنا</h3>
                <p className="text-gray-400 leading-relaxed text-lg mb-4">
                  نعمل من قلب <span className="text-white font-semibold">طرابلس</span>، عاصمة ليبيا، 
                  حيث نخدم عملاءنا من جميع أنحاء المدينة. موقعنا الاستراتيجي يسهل الوصول إلينا 
                  من أي مكان في طرابلس.
                </p>
                <div className="flex items-center gap-3 text-gray-300 mt-6">
                  <Clock01Icon size={20} className="text-primary" />
                  <span className="font-medium">نعمل يومياً من 8 صباحاً حتى 8 مساءً</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative p-8 rounded-3xl bg-[#111] border border-white/5 overflow-hidden group hover:border-primary/30 transition-all duration-300"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-[80px] pointer-events-none group-hover:bg-primary/10 transition-colors" />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target01Icon size={28} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">رؤيتنا</h3>
              <p className="text-gray-400 leading-relaxed">
                أن نكون الوجهة الأولى والأفضل في طرابلس للعناية الفاخرة بالسيارات، 
                وأن نضع معايير جديدة للجودة والاحترافية في السوق الليبي.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative p-8 rounded-3xl bg-[#111] border border-white/5 overflow-hidden group hover:border-primary/30 transition-all duration-300"
          >
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-[80px] pointer-events-none group-hover:bg-primary/10 transition-colors" />
            <div className="relative z-10">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <EyeIcon size={28} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">مهمتنا</h3>
              <p className="text-gray-400 leading-relaxed">
                تقديم خدمات عناية بالسيارات بمعايير عالمية، مع الحفاظ على القيم الليبية الأصيلة 
                من الكرم والصدق والالتزام. نؤمن بأن كل عميل يستحق الأفضل.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          className="mb-24"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-3xl md:text-4xl font-black text-white text-center mb-12">
            قيمنا <span className="text-primary">الأساسية</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 rounded-2xl bg-[#111] border border-white/5 overflow-hidden group hover:border-primary/30 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-gray-400 group-hover:text-primary group-hover:bg-primary/20 transition-all">
                    {value.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors">
                    {value.title}
                  </h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Milestones Timeline */}
        <motion.div
          className="mb-24"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-3xl md:text-4xl font-black text-white text-center mb-12">
            محطات <span className="text-primary">رحلة</span> نجاحنا
          </h3>
          <div className="relative max-w-4xl mx-auto">
            {/* Timeline Line */}
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-gradient-to-b from-primary via-primary/50 to-transparent hidden md:block" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-[#050505] z-10 hidden md:block" />
                  
                  {/* Content Card */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                    <div className="relative p-6 rounded-2xl bg-[#111] border border-white/5 overflow-hidden group hover:border-primary/30 transition-all">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-[60px] pointer-events-none group-hover:bg-primary/10 transition-colors" />
                      <div className="relative z-10">
                        <div className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-bold mb-3">
                          {milestone.year}
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">{milestone.title}</h4>
                        <p className="text-gray-400 text-sm leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Empty space for alignment */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}

